const discountStates = new Map();
const discountEvents = new WeakSet();
let cartRevision = 0;

document.addEventListener('cart:change', (event) => {
  cartRevision += 1;
  for (const state of discountStates.values()) {
    if (!discountEvents.has(event)) {
      state.restoreFocus = document.activeElement === state.owner.input;
      if (!state.error) state.message = '';
    }
    if (Array.isArray(event.detail?.cart?.discount_codes)) {
      state.owner.setCodes(event.detail.cart);
    } else {
      state.codes = null;
      state.codesRevision = -1;
      queueMicrotask(() => state.owner.hydrate());
    }
    state.owner.render();
  }
}, { capture: true });

class MtCartDiscount extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    this.details = this.querySelector('[data-discount-details]');
    this.input = this.querySelector('[data-discount-input]');
    this.status = this.querySelector('[data-discount-status]');
    this.codes = this.querySelector('[data-discount-codes]');
    this.codeTemplate = this.querySelector('[data-discount-code-template]');
    if (!this.details || !this.input || !this.status || !this.dataset.sectionId || !this.dataset.updateUrl || !this.dataset.cartUrl) return;
    const previous = discountStates.get(this.dataset.sectionId);
    this.state = previous || { busy: false, message: '', error: false };
    this.state.owner = this;
    discountStates.set(this.dataset.sectionId, this.state);
    if (previous) {
      this.details.open = Boolean(this.state.open);
      this.input.value = this.state.value || '';
    }
    this.render();
    this.hydrate();
    this.addEventListener('submit', (event) => {
      if (!event.target.matches('[data-discount-form]')) return;
      event.preventDefault();
      this.update();
    }, { signal: this.abort.signal });
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-discount-remove]');
      if (button && this.contains(button)) {
        event.preventDefault();
        this.update(button.dataset.discountRemove);
      }
    }, { signal: this.abort.signal });
    this.addEventListener('input', () => this.capture(), { signal: this.abort.signal });
    this.details.addEventListener('toggle', () => this.capture(), { signal: this.abort.signal });
    if (this.state.restoreFocus) {
      this.state.restoreFocus = false;
      queueMicrotask(() => {
        if (this.isConnected && (document.activeElement === document.body || this.contains(document.activeElement))) this.input.focus({ preventScroll: true });
      });
    }
  }

  disconnectedCallback() {
    this.capture();
    this.abort?.abort();
    const state = this.state;
    queueMicrotask(() => {
      if (state && state.owner === this && !this.isConnected && !state.busy) discountStates.delete(this.dataset.sectionId);
    });
  }

  capture() {
    if (!this.state || this.state.owner !== this) return;
    this.state.open = this.details.open;
    this.state.value = this.input.value;
  }

  render() {
    const state = this.state;
    if (!state || state.owner !== this || !this.isConnected) return;
    const codes = state.codes || [];
    const signature = JSON.stringify(codes);
    if (this.codes && this.codeTemplate?.content.firstElementChild && this.renderedCodes !== signature) {
      this.codes.replaceChildren(...codes.map((code) => {
        const chip = this.codeTemplate.content.firstElementChild.cloneNode(true);
        chip.querySelector('[data-discount-code-label]').textContent = code;
        const button = chip.querySelector('[data-discount-remove]');
        button.dataset.discountRemove = code;
        button.setAttribute('aria-label', `${button.getAttribute('aria-label')} ${code}`);
        return chip;
      }));
      this.renderedCodes = signature;
    }
    if (this.codes) this.codes.hidden = !codes.length;
    this.status.textContent = state.message;
    this.status.hidden = !state.message;
    this.status.toggleAttribute('data-error', state.error);
    this.querySelectorAll('[data-discount-submit], [data-discount-remove]').forEach((button) => {
      button.disabled = state.busy;
      button.setAttribute('aria-busy', String(state.busy));
    });
    this.input.readOnly = state.busy;
    this.input.setAttribute('aria-invalid', String(state.error));
  }

  acceptedCodes(cart) {
    if (!Array.isArray(cart.discount_codes)) throw new Error(this.dataset.errorNetwork);
    return cart.discount_codes.filter((entry) => entry?.applicable === true && typeof entry.code === 'string' && entry.code.trim()).map((entry) => entry.code);
  }

  setCodes(cart) {
    this.state.codes = this.acceptedCodes(cart);
    this.state.codesRevision = cartRevision;
  }

  async hydrate() {
    const state = this.state;
    if (!state || state.owner !== this || !this.isConnected || state.busy || this.hydrating === this.abort.signal || state.codesRevision === cartRevision) return;
    const signal = this.abort.signal;
    this.hydrating = signal;
    try {
      let cart;
      let revision;
      do {
        ({ cart, revision } = await this.readSnapshot(signal));
        if (signal.aborted || state.owner !== this || !this.isConnected || state.busy || state.codesRevision === cartRevision) return;
      } while (revision !== cartRevision);
      this.setCodes(cart);
      this.render();
    } catch {
      // Hydration is read-only; retain any existing operation error and allow a later retry.
    } finally {
      if (this.hydrating === signal) this.hydrating = null;
    }
  }

  async readCart(signal) {
    const response = await fetch(this.dataset.cartUrl, { signal, cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(this.dataset.errorNetwork);
    const cart = await response.json();
    if (!Array.isArray(cart.items) || !Number.isInteger(cart.item_count)) throw new Error(this.dataset.errorNetwork);
    return cart;
  }

  async readSnapshot(signal) {
    let revision;
    let cart;
    do {
      revision = cartRevision;
      cart = await this.readCart(signal);
      if (signal?.aborted || !this.state.owner.isConnected) throw new Error(this.dataset.errorNetwork);
    } while (revision !== cartRevision);
    return { cart, revision };
  }

  async publish(cart, revision, setResult) {
    const sectionId = this.dataset.sectionId;
    const valid = (markup) => {
      if (typeof markup !== 'string') return false;
      const drawer = new DOMParser().parseFromString(markup, 'text/html').querySelector('cart-drawer');
      return Boolean(drawer && (cart.item_count === 0 ? drawer.querySelector('.empty-state') : drawer.querySelector('.cart-drawer__inner') && drawer.querySelector('[slot="footer"]')));
    };
    while (true) {
      if (!this.state.owner.isConnected) throw new Error(this.dataset.errorNetwork);
      if (revision !== cartRevision) ({ cart, revision } = await this.readSnapshot());
      cart.sections ||= {};
      if (!valid(cart.sections[sectionId])) {
        const url = new URL(window.location.href);
        url.searchParams.set('sections', sectionId);
        url.searchParams.delete('section_id');
        const response = await fetch(url.pathname + url.search, { cache: 'no-store', headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(this.dataset.errorNetwork);
        const sections = await response.json();
        if (!this.state.owner.isConnected) throw new Error(this.dataset.errorNetwork);
        if (revision !== cartRevision) continue;
        if (!valid(sections[sectionId])) throw new Error(this.dataset.errorNetwork);
        cart.sections[sectionId] = sections[sectionId];
      }
      if (revision !== cartRevision) continue;
      setResult?.(cart);
      const state = this.state;
      state.owner.capture();
      state.restoreFocus = Boolean(state.focusAtSubmit && (state.owner.contains(document.activeElement) || (document.activeElement === document.body && (!state.focusAtSubmit.isConnected || state.focusAtSubmit.disabled))));
      state.busy = false;
      const event = new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'discount:change', cart } });
      discountEvents.add(event);
      document.documentElement.dispatchEvent(event);
      return;
    }
  }

  async update(removeCode) {
    const state = this.state;
    if (!state || state.owner !== this || !this.isConnected || state.busy) return;
    const removing = typeof removeCode === 'string';
    const code = (removing ? removeCode : this.input.value).trim();
    state.error = !code || code.includes(',');
    state.message = (!code ? this.dataset.errorEmpty : code.includes(',') ? this.dataset.errorInvalid : '') || '';
    if (state.error) return this.render();
    this.capture();
    state.focusAtSubmit = this.contains(document.activeElement) ? document.activeElement : null;
    state.busy = true;
    this.render();
    let attempted = false;
    try {
      const { cart: before, revision } = await this.readSnapshot();
      const codes = this.acceptedCodes(before).filter((existing) => !removing || existing.toLowerCase() !== code.toLowerCase());
      if (!removing && !codes.some((existing) => existing.toLowerCase() === code.toLowerCase())) codes.push(code);
      const sections = [this.dataset.sectionId];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
      if (!state.owner.isConnected) throw new Error(this.dataset.errorNetwork);
      attempted = true;
      const response = await fetch(this.dataset.updateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ discount: codes.join(','), sections: [...new Set(sections)].slice(0, 5), sections_url: window.location.pathname + window.location.search })
      });
      if (!response.ok) throw new Error(this.dataset.errorNetwork);
      const cart = await response.json();
      if (!Array.isArray(cart.items) || !Number.isInteger(cart.item_count)) throw new Error(this.dataset.errorNetwork);
      await this.publish(cart, revision, (latest) => {
        const accepted = this.acceptedCodes(latest).some((existing) => existing.toLowerCase() === code.toLowerCase());
        state.error = removing ? accepted : !accepted;
        state.message = ((state.error ? this.dataset.errorInvalid : removing ? this.dataset.messageRemoved : this.dataset.messageApplied) || '').replaceAll('[code]', code);
      });
    } catch {
      state.error = true;
      state.message = this.dataset.errorNetwork;
      if (attempted && state.owner.isConnected) {
        try {
          const { cart, revision } = await this.readSnapshot();
          await this.publish(cart, revision);
        } catch { state.restoreFocus = false; }
      }
    } finally {
      state.busy = false;
      state.owner.render();
      state.focusAtSubmit = null;
      if (!state.owner.isConnected) discountStates.delete(this.dataset.sectionId);
      else state.owner.hydrate();
    }
  }
}

if (!customElements.get('mt-cart-discount')) customElements.define('mt-cart-discount', MtCartDiscount);
